// ═══════════════════════════════════════════════════════════════
// HOOK: useCompanies
// Gestión de compañías - CRUD completo
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { companiesAPI } from '../api/companies';

export function useCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar todas las compañías
  const loadCompanies = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await companiesAPI.getAll(filters);
      setCompanies(data);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError(err.response?.data?.error || 'Error al cargar compañías');
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear nueva compañía
  const createCompany = async (companyData) => {
    try {
      setLoading(true);
      setError(null);
      const newCompany = await companiesAPI.create(companyData);
      setCompanies(prev => [...prev, newCompany]);
      return { success: true, data: newCompany };
    } catch (err) {
      console.error('Error creating company:', err);
      const errorMsg = err.response?.data?.error || 'Error al crear compañía';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Actualizar compañía
  const updateCompany = async (id, companyData) => {
    try {
      setLoading(true);
      setError(null);
      const updated = await companiesAPI.update(id, companyData);
      setCompanies(prev => prev.map(c => c.id === id ? updated : c));
      return { success: true, data: updated };
    } catch (err) {
      console.error('Error updating company:', err);
      const errorMsg = err.response?.data?.error || 'Error al actualizar compañía';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Desactivar compañía
  const deleteCompany = async (id) => {
    try {
      setLoading(true);
      setError(null);
      await companiesAPI.delete(id);
      setCompanies(prev => prev.map(c =>
        c.id === id ? { ...c, estado: 'inactiva' } : c
      ));
      return { success: true };
    } catch (err) {
      console.error('Error deleting company:', err);
      const errorMsg = err.response?.data?.error || 'Error al desactivar compañía';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Obtener compañía por ID
  const getCompanyById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const data = await companiesAPI.getById(id);
      return { success: true, data };
    } catch (err) {
      console.error('Error getting company:', err);
      const errorMsg = err.response?.data?.error || 'Error al obtener compañía';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Obtener usuarios de una compañía
  const getCompanyUsers = async (companyId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await companiesAPI.getUsers(companyId);
      return { success: true, data };
    } catch (err) {
      console.error('Error getting company users:', err);
      const errorMsg = err.response?.data?.error || 'Error al obtener usuarios';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Asignar usuario a compañía
  const assignUser = async (companyId, userId, data) => {
    try {
      setLoading(true);
      setError(null);
      await companiesAPI.assignUser(companyId, userId, data);
      return { success: true };
    } catch (err) {
      console.error('Error assigning user:', err);
      const errorMsg = err.response?.data?.error || 'Error al asignar usuario';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Remover usuario de compañía
  const removeUser = async (companyId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await companiesAPI.removeUser(companyId, userId);
      return { success: true };
    } catch (err) {
      console.error('Error removing user:', err);
      const errorMsg = err.response?.data?.error || 'Error al remover usuario';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  return {
    companies,
    loading,
    error,
    loadCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    getCompanyById,
    getCompanyUsers,
    assignUser,
    removeUser
  };
}

export default useCompanies;
